import Axios, { AxiosRequestConfig, AxiosInstance, AxiosError, AxiosResponse } from "axios";
import { throttleAdapterEnhancer } from "axios-extensions";

interface IAxiosInterceptors {
  request?: () => void;
  requestError?: () => void;
  response?: () => void;
  responseError?: () => void;
}

export default class Singleton {
  private static instance: Singleton;
  private axios!: AxiosInstance;
  private _options?: AxiosRequestConfig;

  private constructor() {}

  static getInstance() {
    if (!Singleton.instance) {
      Singleton.instance = new Singleton();
      Singleton.instance.axios = Axios.create({
        adapter: throttleAdapterEnhancer(Axios.defaults.adapter!, {
          threshold: 2000,
          // enabledByDefault: false,
        }),
      });
    }
    return Singleton.instance;
  }

  setInterceptor(interceprots: IAxiosInterceptors) {
    this.axios.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        // Do something before request is sent
        if (interceprots.request) {
          // app.$Progress.start()
          interceprots.request();
        }
        return config;
      },
      (error: AxiosError) => {
        if (interceprots.requestError) {
          // app.$Progress.finish()
          interceprots.requestError();
        }
        return Promise.reject(error);
      }
    );

    this.axios.interceptors.response.use(
      // Do something with response data
      (response: AxiosResponse) => {
        if (interceprots.response) {
          // app.$Progress.finish()
          interceprots.response();
        }
        return response;
      },
      (error: AxiosError) => {
        if (interceprots.responseError) {
          // app.$Progress.fail()
          interceprots.responseError();
        }
        return Promise.reject(error);
      }
    );
  }

  setAppToken(appAccessToken: string | undefined) {
    if (appAccessToken) {
      this.axios.defaults.headers.common["app-token"] = appAccessToken;
    }
  }

  setAuthorizationToken(authorizationToken: string | undefined) {
    if (authorizationToken) {
      this.axios.defaults.headers.common["Authorization"] = authorizationToken;
    }
  }

  setOptions(options?: AxiosRequestConfig) {
    const existOptions = this._options;
    this._options = { ...existOptions, ...options };

    if (this._options.baseURL) {
      this.axios.defaults.baseURL = this._options.baseURL;
    }
  }

  cleanAuthorization() {
    delete this.axios.defaults.headers.common["Authorization"];
  }

  getOptions() {
    return this._options;
  }

  async get(url: string, options?: AxiosRequestConfig) {
    return await this.axios.get(url, options);
  }

  async post(url: string, data?: any, options?: AxiosRequestConfig) {
    return await this.axios.post(url, data, options);
  }

  async put(url: string, data?: any, options?: AxiosRequestConfig) {
    return await this.axios.put(url, data, options);
  }

  async delete(url: string, options?: AxiosRequestConfig) {
    return await this.axios.delete(url, options);
  }
}
